import {
  Button,
  ButtonGroup,
  Modal,
  TextBody,
  TextCaption,
  TextTitle,
} from '@pypestream/design-system';
import { useTranslation } from '@pypestream/translations';
import { classicImportSummary } from '@pypestream/ui-patterns';
import { useEffect, useState } from 'react';

import {
  type ClassicSecretsSource,
  fetchClassicSecretsSource,
  importClassicSecrets,
  type MaestroError,
  reasonFor,
} from './maestro-org-api';
import { useSecretsText } from './org-secrets-text';

/**
 * Root org, Super Admin only: copies the admin's classic (LiveView) org secrets into the
 * Pypestream org, prefixed and unmarked. Hidden when the server names no source.
 */
export function ClassicSecretsImport({
  orgId,
  onImported,
}: {
  orgId: string;
  onImported: (summary: string) => void;
}) {
  const { t } = useTranslation();
  const text = useSecretsText();
  const [source, setSource] = useState<ClassicSecretsSource | null>(null);
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<MaestroError | null>(null);

  useEffect(() => {
    let current = true;
    fetchClassicSecretsSource(orgId).then(
      (found) => current && setSource(found),
      () => current && setSource(null)
    );
    return () => {
      current = false;
    };
  }, [orgId]);

  if (!source) return null;
  const sourceName = source.name
    ? text('classic.source', { name: source.name, id: source.maestroProjectId })
    : text('classic.sourceUnnamed', { id: source.maestroProjectId });

  const copy = async () => {
    setAsking(false);
    setBusy(true);
    setError(null);
    try {
      const outcome = await importClassicSecrets(orgId);
      const summary = classicImportSummary(
        outcome,
        (part, { count, names, more }) =>
          text(`classic.summary.${part}`, {
            count,
            names: more > 0 ? text('classic.more', { names, more }) : names,
          })
      );
      onImported(summary.text);
    } catch (e) {
      setError(reasonFor(e).reason);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        variant="secondary"
        loading={busy}
        disabled={busy}
        test-id="org-secrets-classic"
        onClick={() => setAsking(true)}
      >
        {text('classic.button')}
      </Button>
      {error && (
        <TextCaption variant="danger" role="alert">
          {t(`manager/organizations:orgTabs.errors.${error}`)}
        </TextCaption>
      )}
      <Modal
        open={asking}
        size="medium"
        test-id="org-secrets-classic-confirm"
        onClose={() => setAsking(false)}
      >
        <TextTitle size="small" slot="header">
          {text('classic.title')}
        </TextTitle>
        <TextBody>
          {text('classic.body', {
            count: source.secretCount,
            source: sourceName,
          })}
        </TextBody>
        <ButtonGroup slot="footer">
          <Button variant="secondary" onClick={() => setAsking(false)}>
            {text('delete.cancel')}
          </Button>
          <Button
            test-id="org-secrets-classic-copy"
            onClick={() => void copy()}
          >
            {text('classic.confirm')}
          </Button>
        </ButtonGroup>
      </Modal>
    </>
  );
}
